// components/admin/PendingRequests/index.jsx

import React, { useState, useEffect } from "react";
import { subscriptionService } from "../../../services/subscription.service";
import { Check, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "../../../utils/format";
import { SUBSCRIPTION_STATUS_LABELS } from "../../../config/constants";
import {
	RequestsContainer,
	RequestsHeader,
	RequestsList,
	RequestCard,
	CardHeader,
	CardBody,
	CardFooter,
	StatusBadge,
	PlanDetails,
	ChildInfo,
	PriceInfo,
	InfoRow,
	UserDetails,
	EmptyState,
	ActionButtons,
} from "./styles";
import { Button } from "../../../styles/components";

const PendingRequests = () => {
	const [requests, setRequests] = useState([]);
	const [loading, setLoading] = useState(true);
	const [processingId, setProcessingId] = useState(null);

	const fetchRequests = async () => {
		try {
			setLoading(true);
			const data = await subscriptionService.getAllRequests();
			setRequests(data);
		} catch (error) {
			console.error("Error fetching requests:", error);
			toast.error("Failed to load subscription requests", {
				style: { padding: "1rem" },
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchRequests();
	}, []);

	const handleApprove = async (request) => {
		setProcessingId(request.id);
		try {
			await subscriptionService.approveRequest(request.id);
			toast.success("Request approved successfully", {
				style: { padding: "1rem" },
			});
			fetchRequests();
		} catch (error) {
			toast.error(error.message || "Failed to approve request", {
				style: { padding: "1rem" },
			});
		} finally {
			setProcessingId(null);
		}
	};

	const handleReject = async (requestId) => {
		setProcessingId(requestId);
		try {
			await subscriptionService.rejectRequest(requestId);
			toast.success("Request rejected", {
				style: { padding: "1rem" },
			});
			fetchRequests();
		} catch (error) {
			toast.error(error.message || "Failed to reject request", {
				style: { padding: "1rem" },
			});
		} finally {
			setProcessingId(null);
		}
	};

	if (loading) {
		return (
			<RequestsContainer>
				<EmptyState>Loading requests...</EmptyState>
			</RequestsContainer>
		);
	}

	if (requests.length === 0) {
		return (
			<RequestsContainer>
				<EmptyState>
					<AlertCircle size={48} />
					<h3>No Pending Requests</h3>
					<p>There are no subscription requests to review.</p>
				</EmptyState>
			</RequestsContainer>
		);
	}

	return (
		<RequestsContainer>
			<RequestsHeader>
				<h2>Subscription Requests</h2>
				<p>{requests.length} requests pending review</p>
			</RequestsHeader>

			<RequestsList>
				{requests.map((request) => (
					<RequestCard key={request.id}>
						<CardHeader>
							<UserDetails>
								<h3>{request.userData?.name || "Unknown User"}</h3>
								<p>{request.userData?.email}</p>
							</UserDetails>
							<StatusBadge status={request.status}>
								{SUBSCRIPTION_STATUS_LABELS[request.status]}
							</StatusBadge>
						</CardHeader>

						<CardBody>
							<PlanDetails>
								<h4>{request.plan.name} Plan</h4>
								<p>
									{request.plan.periodCredits} credits per{" "}
									{request.plan.interval}
								</p>
							</PlanDetails>

							<ChildInfo>
								<span>Children: {request.childCount}</span>
								{request.discount > 0 && (
									<span className="discount">
										({request.discount}% discount applied)
									</span>
								)}
							</ChildInfo>

							<PriceInfo>
								<InfoRow>
									<span>Base Price:</span>
									<span>{formatCurrency(request.basePrice)}</span>
								</InfoRow>
								{request.discount > 0 && (
									<InfoRow className="discount">
										<span>Discount:</span>
										<span>
											-{formatCurrency(request.basePrice - request.finalPrice)}
										</span>
									</InfoRow>
								)}
								<InfoRow className="total">
									<span>Final Price:</span>
									<span>{formatCurrency(request.finalPrice)}</span>
								</InfoRow>
							</PriceInfo>
						</CardBody>

						<CardFooter>
							<ActionButtons>
								<Button
									variant="outlined"
									onClick={() => handleReject(request.id)}
									disabled={processingId === request.id}
								>
									<X size={18} />
									Reject
								</Button>
								<Button
									onClick={() => handleApprove(request)}
									disabled={processingId === request.id}
								>
									<Check size={18} />
									Approve
								</Button>
							</ActionButtons>
						</CardFooter>
					</RequestCard>
				))}
			</RequestsList>
		</RequestsContainer>
	);
};

export default PendingRequests;
